import React, { useEffect, useState, useCallback } from "react";
import { Toast } from "primereact/toast";
import { getImpersonationCandidates, startImpersonation, stopImpersonation } from "../api/admin";
import "../css/AdminImpersonation.css";

function AdminImpersonation({ refreshSession, sessionDetails, onImpersonationStart, currentUsername }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionInProgress, setActionInProgress] = useState(null);
  const toastRef = React.useRef(null);

  const showToast = useCallback((severity, summary, detail) => {
    toastRef.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getImpersonationCandidates();
      setCandidates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching impersonation candidates:", err);
      setError(err.message || "Failed to load impersonation candidates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleImpersonate = async (targetUsername) => {
    setActionInProgress(targetUsername);
    try {
      const data = await startImpersonation(targetUsername);
      showToast("success", "Impersonation started", `Now impersonating ${targetUsername}`);
      onImpersonationStart?.({ ...data, targetUsername, adminUsername: currentUsername });
    } catch (err) {
      console.error("Error starting impersonation:", err);
      showToast(
        "error",
        "Error",
        err.message || "Could not start impersonation."
      );
    } finally {
      setActionInProgress(null);
    }
  };

  const handleStop = async () => {
    setActionInProgress("__stop__");
    try {
      await stopImpersonation();
      showToast("success", "Impersonation ended", "Returned to admin session.");
      await refreshSession();
    } catch (err) {
      console.error("Error stopping impersonation:", err);
      showToast(
        "error",
        "Error",
        err.message || "Could not stop impersonation."
      );
    } finally {
      setActionInProgress(null);
    }
  };

  return (
    <div className="admin-impersonation-container">
      <Toast ref={toastRef} position="top-center" />
      <div className="admin-impersonation-card">
        <div className="admin-impersonation-header">
          <h2>Impersonate a User</h2>
          <p>Choose a user below to temporarily impersonate their session.</p>
          {sessionDetails?.impersonating && sessionDetails?.impersonatedUsername && (
            <div className="admin-impersonation-current">
              Currently impersonating{" "}
              <strong>{sessionDetails.impersonatedUsername}</strong>
            </div>
          )}
          <div className="admin-impersonation-actions">
            <button
              className="admin-impersonation-refresh"
              onClick={fetchCandidates}
              disabled={loading}
              type="button"
            >
              Refresh
            </button>
            <button
              className="admin-impersonation-stop"
              onClick={handleStop}
              disabled={actionInProgress === "__stop__" || !sessionDetails?.impersonating}
              type="button"
            >
              {actionInProgress === "__stop__"
                ? "Stopping..."
                : sessionDetails?.impersonating
                ? "Stop impersonating"
                : "Not impersonating"}
            </button>
          </div>
        </div>

        {loading && <div className="admin-impersonation-status">Loading users…</div>}
        {error && <div className="admin-impersonation-error">{error}</div>}

        {!loading && !error && candidates.length === 0 && (
          <div className="admin-impersonation-status">
            No users available for impersonation.
          </div>
        )}

        {!loading && !error && candidates.length > 0 && (
          <div className="admin-impersonation-list">
            {candidates.map((candidate) => {
              const displayName =
                candidate.displayName ||
                candidate.fullName ||
                candidate.name ||
                candidate.username ||
                candidate.email;

              const identifier =
                candidate.username || candidate.email || candidate.id || "";

              return (
                <div className="admin-impersonation-item" key={identifier}>
                  <div className="admin-impersonation-item__info">
                    <div className="admin-impersonation-item__primary">
                      {displayName || "Unknown user"}
                    </div>
                    {candidate.email && (
                      <div className="admin-impersonation-item__secondary">
                        {candidate.email}
                      </div>
                    )}
                    {candidate.accountCount != null && (
                      <div className="admin-impersonation-item__meta">
                        Accounts: {candidate.accountCount}
                      </div>
                    )}
                  </div>
                  <button
                    className="admin-impersonation-item__button"
                    type="button"
                    onClick={() => handleImpersonate(identifier)}
                    disabled={actionInProgress === identifier}
                  >
                    {actionInProgress === identifier ? "Starting..." : "Impersonate"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminImpersonation;

